import React, { useState, useCallback } from "react";
import elementSchema from "schemas";
import { Modal, Button } from "@alm/flow-ds/components";
import { useTranslation } from "react-i18next";
import { ElementTypeList } from "schemas/elements";
import ElementsList from "./ElementsList";
import ElementsListItem from "./ElementsItem";
import useScrollingListbox from "hooks/useScrollingListbox";

type Props = {
	newElement: (data: ElementTypeList) => void;
	isNested?: boolean;
	nestedField?: "search";
	isEmu?: boolean;
};

export default function ElementsDialog({
	newElement,
	isNested,
	nestedField,
	isEmu,
}: Props) {
	const { t } = useTranslation();
	const [isOpen, setIsOpen] = useState(false);
	const [initFocus, setInitFocus] = useState(false);

	const {
		currentActive,
		setCurrentActive,
		handleKeyDown,
		refCallback,
		bodyRef,
	} = useScrollingListbox(
		Object.keys(elementSchema).length,
		"elements-dialog-list",
		() => handleOnClick(activeElement)
	);

	let activeElement: ElementTypeList;

	const handleOnClick = useCallback(
		(element: ElementTypeList) => {
			newElement(element);
			setIsOpen(false);
			setCurrentActive(-1);
			setInitFocus(false);
		},
		[newElement, setCurrentActive]
	);

	return (
		<>
			<Button
				className={`btn-tertiary btn-lg add-btn${
					isNested ? " nested" : ""
				}`}
				label={
					isNested
						? nestedField === "search"
							? t("formDesigner.labels.addView")
							: t("formDesigner.labels.addNested")
						: t("formDesigner.labels.addElement")
				}
				onClick={() => setIsOpen(true)}
			/>
			<Modal
				className="elements-dialog"
				closeAriaLabel={"Close"}
				close={() => {
					setIsOpen(false);
					setInitFocus(false);
					setCurrentActive(-1);
				}}
				isOpen={isOpen}
				title={
					isNested
						? nestedField === "search"
							? t("formDesigner.dialogs.elementsViewTitle")
							: t("formDesigner.dialogs.elementsNestedTitle")
						: t("formDesigner.dialogs.elementsTitle")
				}
				modalSize="sm">
				<ElementsList
					name="elements-dialog"
					currentActive={currentActive}
					onKeyDown={handleKeyDown}
					multiselect={false}
					forwardedRef={bodyRef}
					handleFocus={() => {
						if (!initFocus) setCurrentActive(0);
						setInitFocus(true);
					}}>
					{Object.keys(elementSchema)
						.filter(element => {
							return (
								element !== "multiReferenceView" &&
								element !== "nestedView"
							);
						})
						.map((element, index) => {
							if (
								(((isEmu && element !== "legacyArrayView") ||
									!isEmu) &&
									nestedField === "search" &&
									(element === "text" ||
										element === "lookup" ||
										element === "select" ||
										element === "date")) ||
								nestedField !== "search"
							) {
								// check if the index of the element matches the currently focussed item
								const active = currentActive === index;
								if (active) {
									activeElement = element as ElementTypeList;
								}
								return (
									<ElementsListItem
										key={`item__${index}`}
										name="elements-dialog"
										onMouseDown={() => {
											setInitFocus(true);
											setCurrentActive(index);
										}}
										onClick={() => {
											handleOnClick(
												element as ElementTypeList
											);
										}}
										index={index}
										forwardedRef={refCallback}
										initFocus={initFocus}
										active={active}
										// selected and focus is the same as active for a single select
										selected={active}
										focus={active}>
										{t(
											`formDesigner.elements.${
												elementSchema[
													element as ElementTypeList
												].elem.displayName
											}`
										)}
									</ElementsListItem>
								);
							} else {
								return null;
							}
						})}
				</ElementsList>
			</Modal>
		</>
	);
}
